import {
  IonContent,
  IonPage,
  IonButton,
  IonCard,
  IonCardContent,
  IonSpinner,
} from '@ionic/react';
import { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { useWallet } from '../contexts/WalletContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { playCelebrationSound } from '../utils/soundUtils';
import './MemoryMatch.css';

interface Card {
  id: number;
  value: number;
  flipped: boolean;
  matched: boolean;
}

const MemoryMatch: React.FC = () => {
  const { earnCoins } = useWallet();
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [level, setLevel] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const canFlipRef = useRef(true);

  const GRID_SIZES = [4, 6, 8]; // 2x2, 3x3, 4x4 grids (pairs)
  const COINS_PER_LEVEL = [50, 100, 200];

  const initializeGame = (gridSize: number) => {
    const totalCards = gridSize * gridSize;
    const pairs = totalCards / 2;
    const values: number[] = [];
    
    // Generate pairs
    for (let i = 1; i <= pairs; i++) {
      values.push(i, i);
    }
    
    // Shuffle
    for (let i = values.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [values[i], values[j]] = [values[j], values[i]];
    }
    
    const newCards: Card[] = values.map((value, index) => ({
      id: index,
      value,
      flipped: false,
      matched: false,
    }));
    
    setCards(newCards);
    setFlippedCards([]);
    setMoves(0);
    setMatches(0);
    setGameOver(false);
    canFlipRef.current = true;
  };

  useEffect(() => {
    if (gameStarted) {
      const gridSize = GRID_SIZES[Math.min(level - 1, GRID_SIZES.length - 1)];
      initializeGame(gridSize);
    }
  }, [level, gameStarted]);

  const handleCardClick = (cardId: number) => {
    if (!canFlipRef.current || gameOver) return;
    
    const card = cards[cardId];
    if (card.flipped || card.matched || flippedCards.length >= 2) return;

    const newCards = [...cards];
    newCards[cardId].flipped = true;
    setCards(newCards);
    
    const newFlipped = [...flippedCards, cardId];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      canFlipRef.current = false;
      setMoves(moves + 1);
      
      const [firstId, secondId] = newFlipped;
      const firstCard = newCards[firstId];
      const secondCard = newCards[secondId];

      if (firstCard.value === secondCard.value) {
        // Match!
        setTimeout(() => {
          const updatedCards = [...newCards];
          updatedCards[firstId].matched = true;
          updatedCards[secondId].matched = true;
          setCards(updatedCards);
          setFlippedCards([]);
          setMatches(matches + 1);
          canFlipRef.current = true;
          
          // Check if game is complete
          const allMatched = updatedCards.every(c => c.matched);
          if (allMatched) {
            handleGameComplete();
          }
        }, 500);
      } else {
        // No match - flip back
        setTimeout(() => {
          const updatedCards = [...newCards];
          updatedCards[firstId].flipped = false;
          updatedCards[secondId].flipped = false;
          setCards(updatedCards);
          setFlippedCards([]);
          canFlipRef.current = true;
        }, 1000);
      }
    }
  };

  const handleGameComplete = async () => {
    setGameOver(true);
    playCelebrationSound();
    
    // Confetti
    const duration = 2000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: NodeJS.Timeout = setInterval(function() {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    // Award coins based on level and efficiency
    const baseCoins = COINS_PER_LEVEL[Math.min(level - 1, COINS_PER_LEVEL.length - 1)];
    const efficiencyBonus = moves <= cards.length / 2 ? 50 : 0; // Bonus for efficient play
    const totalCoins = baseCoins + efficiencyBonus;

    try {
      await earnCoins(
        totalCoins,
        `Memory Match Level ${level} - ${moves} moves`,
        'memory-match'
      );
    } catch (error) {
      console.error('Error awarding coins:', error);
    }
  };

  const startGame = () => {
    setGameStarted(true);
    setLevel(1);
  };

  const nextLevel = () => {
    if (level < 3) {
      setLevel(level + 1);
    } else {
      // Restart from level 1
      setLevel(1);
    }
  };

  const resetGame = () => {
    setGameStarted(false);
    setLevel(1);
    setGameOver(false);
  };

  const gridSize = gameStarted ? GRID_SIZES[Math.min(level - 1, GRID_SIZES.length - 1)] : 4;
  const totalPairs = (gridSize * gridSize) / 2;

  return (
    <IonPage>
      <AppHeader title="Memory Match" showBackButton={true} defaultHref="/games" />
      <IonContent fullscreen className="ion-padding">
        <div className="memory-match-container">
          {!gameStarted ? (
            <IonCard>
              <IonCardContent>
                <h2 className="game-title">🧠 Memory Match</h2>
                <p className="game-description">
                  Match pairs of cards by remembering their positions. 
                  Complete all levels to earn maximum coins!
                </p>
                <div className="game-info">
                  <p><strong>Level 1:</strong> 4x4 grid (8 pairs) - 50 coins</p>
                  <p><strong>Level 2:</strong> 6x6 grid (18 pairs) - 100 coins</p>
                  <p><strong>Level 3:</strong> 8x8 grid (32 pairs) - 200 coins</p>
                  <p className="bonus-info">💡 Efficiency bonus: Complete with fewer moves!</p>
                </div>
                <IonButton expand="block" color="primary" onClick={startGame}>
                  Start Game
                </IonButton>
              </IonCardContent>
            </IonCard>
          ) : (
            <>
              <IonCard className="game-stats-card">
                <IonCardContent>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-label">Level</span>
                      <span className="stat-value">{level}/3</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Moves</span>
                      <span className="stat-value">{moves}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Matches</span>
                      <span className="stat-value">{matches}/{totalPairs}</span>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>

              <div className="memory-grid" style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}>
                {cards.map((card) => (
                  <div
                    key={card.id}
                    className={`memory-card ${card.flipped ? 'flipped' : ''} ${card.matched ? 'matched' : ''}`}
                    onClick={() => handleCardClick(card.id)}
                  >
                    <div className="card-front">?</div>
                    <div className="card-back">{card.value}</div>
                  </div>
                ))}
              </div>

              {gameOver && (
                <IonCard className="game-over-card">
                  <IonCardContent>
                    <h2>🎉 Level {level} Complete!</h2>
                    <p>Moves: {moves}</p>
                    <p>Matches: {matches}/{totalPairs}</p>
                    <div className="game-over-actions">
                      {level < 3 ? (
                        <IonButton expand="block" color="primary" onClick={nextLevel}>
                          Next Level
                        </IonButton>
                      ) : (
                        <IonButton expand="block" color="success" onClick={resetGame}>
                          Play Again
                        </IonButton>
                      )}
                      <IonButton expand="block" fill="outline" onClick={resetGame}>
                        Main Menu
                      </IonButton>
                    </div>
                  </IonCardContent>
                </IonCard>
              )}
            </>
          )}
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default MemoryMatch;

